<template>
    <card class="card-plain">
        <form>
            <div class="card-body row pt-0">
                <div class="col-12">
                    <div class="row">
                        <div class="col-lg-6 pt-2">
                            <legend>{{$t('Questionnaire Title')}}</legend>
                            <language-picker v-if="questionnaire.title" v-model="questionnaire.title">
                                <template slot="body" slot-scope="props">
                                    <fg-input :name="`title${props.index}`"
                                              :label="$t('Title')"
                                              autocomplete="off"
                                              required
                                              :error="getError(`title${props.index}`)"
                                              v-model="questionnaire.title[props.index].text"
                                              v-validate="validations.title[props.index]"
                                              :placeholder="$t('Title')">
                                    </fg-input>
                                </template>
                            </language-picker>
                        </div>
                        <div class="col-lg-6 pt-2">
                            <div v-if="questionnaire.timing" class="col-lg-12 p-0">
                                <timing-picker v-model="questionnaire.timing">
                                    <template slot="body">
                                        <label>{{$t('Questionnaire active until')}}</label>
                                        <div class="row">
                                            <div class="col-6">
                                                <el-input-number v-model="questionnaire.triggerAfter.value" controls-position="right" :min="1"/>
                                            </div>
                                            <div class="col-6">
                                                <fg-input name="triggerAfter.type" v-model="questionnaire.triggerAfter.type">
                                                    <el-select class="select-primary"
                                                               size="large"
                                                               name="triggerAfter.type"
                                                               v-model="questionnaire.triggerAfter.type"
                                                               :placeholder="`${$t('Select')}...`">
                                                        <el-option v-for="option in [{key: 'hours', label: $tc('hour', questionnaire.triggerAfter.value)}, {key: 'days', label: $tc('day', questionnaire.triggerAfter.value)}]"
                                                                   :value="option.key"
                                                                   :label="option.label"
                                                                   :key="option.key">
                                                        </el-option>
                                                    </el-select>
                                                </fg-input>
                                            </div>
                                        </div>
                                    </template>
                                </timing-picker>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-12">
                    <fg-input name="questions" :error="getError('questions')" v-validate="validations.questions"
                              v-model="questionnaire.questions">
                        <list class="p-0" v-model="questionnaire.questions"
                              :title="$t('Questions')"
                              :columns="columnsBy.questions"
                              :actions="actionsBy.questions"
                              :remove="(props) => remove('questions', props.$index)"
                              :isDragable="true"/>
                    </fg-input>
                </div>
                <div class="col-12">
                    <hr>
                </div>
                <div class="col-12 p-0">
                    <list v-model="questionnaire.teams"
                          :title="$t('Allocated Teams')"
                          :columns="columnsBy.teams"
                          :actions="actionsBy.teams"
                          :remove="(props) => remove('teams', props.$index)"/>
                </div>
            </div>
            <div class="card-footer text-right actions">
                <p-button round native-type="button" class="btn-wd btn-one-size-dropdown button-distance" :disabled="loading"
                          @click.prevent="$router.push({ name: 'QuestionnaireList' })">{{$t('cancel')}}
                </p-button>
                <p-button round native-type="submit" type="primary" class="btn btn-fill btn-wd btn-one-size-dropdown" :disabled="loading"
                          @click.prevent="save">
                    {{$t('Save')}}
                </p-button>
            </div>
        </form>

        <questionnaire-modal :value="questionnaire" :show="showDialog" :type="modalType" :cancel="cancel"/>

    </card>
</template>

<script>
  import QuestionnaireModel, { Timing } from 'src/store/models/Questionnaire'
  import { Input, Button, Select, Option, Checkbox, InputNumber } from 'element-ui'
  import QuestionnaireModal from './QuestionnaireModal'
  import List from '../../Components/BasicList'
  import InputPicker from '../../Components/InputPicker'
  import TimingPicker from '../../Components/BasicTimingPicker'
  import LanguagePicker from '../../Components/BasicLanguagePicker'

  export default {
    name: 'QuestionnaireForm',

    provide () {
      return {
        $validator: this.$validator,
      }
    },
    components: {
      [Input.name]: Input,
      [Button.name]: Button,
      [Checkbox.name]: Checkbox,
      [Option.name]: Option,
      [Select.name]: Select,
      [InputNumber.name]: InputNumber,
      LanguagePicker,
      TimingPicker,
      List,
      InputPicker,
      QuestionnaireModal
    },

    data () {
      return {
        questionnaire: {},
        loading: false,
        showDialog: false,
        columnsBy: {
          teams: [{ name: 'title', text: 'Title', sortable: true, align: 'left' }],
          questions: [
            { name: 'category.title', text: 'Question Topic', formatter: this.languageFormatter, sortable: false, align: 'left' },
            { name: 'title', text: 'Title', formatter: this.languageFormatter, sortable: false, align: 'left' },
            { name: 'text', text: 'Text', formatter: this.languageFormatter, sortable: false, align: 'left' },
            { name: 'type', text: 'Type', formatter: this.typeFormatter, sortable: false, align: 'left' }
          ]
        },
        actionsBy: {
          teams: [{ label: 'Add Team', click: () => this.openModal('teams') }],
          questions: [{ label: 'Add Question', click: () => this.checkQuestionLocked() }]
        },
        modalType: '',
        validations: {
          title: {
            0: {
              required: true,
              min: 5
            }
          },
          questions: {
            required: true
          }
        }
      }
    },

    async mounted () {
      await this.initData()
    },

    methods: {
      languageFormatter: function (row, column, cellValue) {
        return this.$options.filters.languageString(cellValue)
      },
      typeFormatter: function (row) {
        return this.$t(row.type)
      },
      async initData () {
        if (this.$route.name === 'QuestionnaireCreate') {
          this.questionnaire = new QuestionnaireModel(QuestionnaireModel.defaults(this.$auth.user.customer.settings.contract.enabledLanguages))
        } else {
          await this.fetchQuestionnaire()
        }
      },
      async fetchQuestionnaire () {
        if (!this.questionnaireId) {
          return
        }
        try {
          this.loading = true
          const result = await QuestionnaireModel.api().get(`/questionnaire/${this.questionnaireId}`, { save: false })
          this.questionnaire = new QuestionnaireModel(result.response.data)
          const timing = this.questionnaire.timing
          if (!timing.hasOwnProperty('time') || !timing.hasOwnProperty('repeatEvery') || !timing.hasOwnProperty('repeatAt')) {
            this.questionnaire.timing = new Timing()
          }
        } catch (e) { console.log('e', e)} finally {
          this.loading = false
        }
      },
      getError (fieldName) {
        const error = this.errors.first(fieldName)
        if (error) {
          if (fieldName.startsWith('title')) {
            return this.$t(error.replace(fieldName, 'title'))
          }
          return this.$t(error)
        }
      },
      save: async function () {
        let isValid = await this.isValid()
        if (!isValid) {
          return
        }

        const { title, triggerAfter, timing } = this.questionnaire
        const teams = this.questionnaire.teams.map(({ _id }) => _id)
        const questions = this.questionnaire.questions.map(({ _id }) => _id)
        const data = { title, triggerAfter, timing, teams, questions }
        try {
          if (this.questionnaire._id) {
            const result = await QuestionnaireModel.api().put(`/questionnaire/${this.questionnaireId}`, data, { save: false })
            this.questionnaire = new QuestionnaireModel(result.response.data)

            this.$notify({ message: this.$t('Successfully saved.'), type: 'success' })

          } else {
            const result = await QuestionnaireModel.api().post(`/questionnaire`, data, { save: false })
            this.questionnaire = new QuestionnaireModel(result.response.data)

            if (result.response.data._id) {
              this.$notify({ message: this.$t('Successfully saved.'), type: 'success' })
              this.$router.push({ name: 'QuestionnaireEdit', params: { id: result.response.data._id } })
            }
          }
        } catch (e) {
          this.$notify({ message: e.message, type: 'danger' })
        }
      },
      isValid: async function () {
        let isValid = await this.$validator.validateAll()
        const validationArray = this.$children.map(child => child.$validator.validateAll())
        const v = await window.Promise.all(validationArray)
        v.some(element => { if (element === false) {isValid = false} })
        return isValid
      },
      openModal: async function (type) {
        this.showDialog = true
        this.modalType = type
      },
      remove: function (type, index) {
        this.questionnaire[type].splice(index, 1)
      },
      cancel: function () {
        this.showDialog = false
      },
      checkQuestionLocked () {
        if (this.questionnaire.locked) {
            this.$dialog.info(this.$t('Information'), this.$t('Can\'t add questions because this is a locked questionnaire.<br>For more informations please contact your administrator'), 'OK')
        } else {
            this.openModal('questions')
        }
      }
    },

    watch: {
      showDialog (val) {
        if (!val) {
          this.modalType = ''
        }
      }
    },

    computed: {
      questionnaireId: function () {
        return this.$route.params.id || ''
      }
    }
  }
</script>

<style lang="scss" scoped>
  .button-distance {
    margin-right: 10px !important;
  }

  @media (max-width: 590px) {
      .btn-one-size-dropdown {
          width: 100%;
          padding-left: 0px;
          padding-right: 0px;
          padding-right: 10px !important;
      }

      .card-footer {
        padding: 0px;
      }

      .button-distance {
        margin-bottom: 10px !important;
      }
    }
</style>
